import React from 'react';
import { Plus, ChevronUp, ChevronDown } from 'lucide-react';
import OsScroll from '../ui/OsScroll';
import { ColumnResizeHandle } from '../useDbColumnResize';
import { sortDir, sortRank } from '../dbGridSort';
import { cellToText } from '../dbCellText';
import { MAX_RENDER_ROWS, ROWNUM_WIDTH_KEY, cellKey } from '../dbGridModel';
import {
  renderCellValue,
  shortColumnType,
  dateColumnKind,
  DateEditButton,
  FkNavigateButton,
} from '../dbGridCells';
import DbEnumCellEditor from './DbEnumCellEditor';

/** The results grid table markup. Plain function called during render, so the element tree is unchanged. */
export function renderResultsGrid({
  result,
  canAddRow,
  editable,
  pendingRows,
  pendingEdits,
  pendingDeletes,
  editingCell,
  editingValue,
  onAddRow,
  onCellOpen,
  onCellContextMenu,
  onBackgroundContextMenu,
  onEditingValueChange,
  onCommitEdit,
  onCancelEdit,
  onEditNull,
  onEditTab,
  sortable,
  sortState,
  onSortColumn,
  selectedRows,
  onToggleRowSelect,
  selectedCells,
  onDatePick,
  rowNumberOffset,
  columnTypes,
  enumByColumn,
  fkByColumn,
  onFkNavigate,
  columnWidths,
  onColumnResize,
  findMatchKeys,
  activeFindKey,
  onHeaderContextMenu,
  loadingMore,
  scrollHostRef,
  anchorCell,
  focusAnchorCell,
  padTop,
  padBottom,
  hasColumnWidths,
  tableElRef,
  typeColors,
  beginResize,
  guideNode,
  colCount,
  isHidden,
  canSelect,
  cellMouseDown,
  cellClass,
  totalResultRows,
  renderRows,
  pendingUnderRow,
  trailingPending,
  renderPendingRow,
}) {
  return (
    <>
    {guideNode}
    {/* The background handler catches right-clicks that no cell claimed (cells
        preventDefault first — the parent checks e.defaultPrevented), so the
        table-level menu (Export/Import) works even over an empty grid. */}
    <OsScroll
      ref={scrollHostRef}
      className={`dbm-grid-wrap${typeColors.rootClass}`}
      style={typeColors.style}
      onContextMenu={onBackgroundContextMenu}
    >
      <table ref={tableElRef} className={hasColumnWidths ? 'dbm-table dbm-table--fixed' : 'dbm-table'}>
        {hasColumnWidths && (
          <colgroup>
            <col
              style={
                columnWidths[ROWNUM_WIDTH_KEY] ? { width: `${columnWidths[ROWNUM_WIDTH_KEY]}px` } : undefined
              }
            />
            {result.columns.map((col) =>
              isHidden(col) ? null : (
                <col key={col} style={columnWidths[col] ? { width: `${columnWidths[col]}px` } : undefined} />
              )
            )}
          </colgroup>
        )}
        <thead>
          <tr>
            <th className="dbm-th dbm-th-rownum">#</th>
            {result.columns.map((col) => {
              if (isHidden(col)) return null;
              const shortType = columnTypes ? shortColumnType(columnTypes[col]) : null;
              return (
                <th
                  key={col}
                  className={sortable ? 'dbm-th dbm-th--sortable' : 'dbm-th'}
                  onClick={sortable ? (e) => onSortColumn(col, e.shiftKey) : undefined}
                  onContextMenu={onHeaderContextMenu ? (e) => onHeaderContextMenu(e, col) : undefined}
                  title={
                    sortable
                      ? 'Sort by this column · Shift+click adds a level · right-click for column options'
                      : undefined
                  }
                >
                  <span className="dbm-th-inner">
                    <span className="dbm-th-label">
                      {col}
                      {shortType && <span className="dbm-th-type">{shortType}</span>}
                    </span>
                    {sortDir(sortState, col) && (
                      <span className="dbm-sort-ind" aria-hidden>
                        {sortDir(sortState, col) === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {/* Rank only when it disambiguates: a single sorted
                            column needs no "1" beside its arrow. */}
                        {sortState.length > 1 && (
                          <span className="dbm-sort-rank">{sortRank(sortState, col)}</span>
                        )}
                      </span>
                    )}
                  </span>
                  {onColumnResize && (
                    <ColumnResizeHandle
                      onBegin={(e) => beginResize(e, col)}
                      onReset={() => onColumnResize(col, null)}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {result.rows.length === 0 && pendingRows.length === 0 ? (
            <tr>
              <td className="dbm-td" colSpan={colCount + 1} style={{ textAlign: 'center' }}>
                No rows
              </td>
            </tr>
          ) : (
            <>
            {/* The rows above the window, as height rather than as elements.
                `aria-hidden` because it is spacing, not a row — a screen reader
                announcing thousands of empty rows would be worse than the DOM
                cost this removes. */}
            {padTop > 0 && <tr aria-hidden style={{ height: padTop }} />}
            {renderRows.map(({ row, index, measure }) => (
              <React.Fragment key={index}>
              <tr
                // The ABSOLUTE row index, so anything hunting for a specific
                // row can ask for it by name instead of counting `<tr>`s —
                // which only agrees with the index while every row is drawn.
                // The scroll restore relies on this; see its frame callback.
                data-row-index={index}
                className={`dbm-tr${selectedRows?.has(index) ? ' dbm-tr--selected' : ''}${
                  pendingDeletes.has(index) ? ' dbm-tr--deleting' : ''
                }`}
              >
                <td
                  className={canSelect ? 'dbm-td dbm-td-rownum dbm-td-rownum--selectable' : 'dbm-td dbm-td-rownum'}
                  onClick={canSelect ? (e) => onToggleRowSelect(index, e) : undefined}
                  title={canSelect ? 'Click to select row · Ctrl/Cmd toggles · Shift selects a range' : undefined}
                >
                  {rowNumberOffset + index + 1}
                </td>
                {row.map((cell, j) => {
                  if (isHidden(result.columns[j])) return null;
                  const isEditing =
                    editable && editingCell && editingCell.rowIndex === index && editingCell.colIndex === j;
                  const editKey = `${index}:${j}`;
                  const isEdited = Object.prototype.hasOwnProperty.call(pendingEdits, editKey);
                  const displayValue = isEdited ? pendingEdits[editKey] : cell;
                  const dtype = onDatePick ? dateColumnKind(columnTypes?.[result.columns[j]]) : null;
                  const fkHere = onFkNavigate ? fkByColumn?.get(result.columns[j]) : null;
                  const findKey = cellKey('r', index, j);
                  const findClass = findMatchKeys.has(findKey)
                    ? findKey === activeFindKey
                      ? ' dbm-td--find-match dbm-td--find-active'
                      : ' dbm-td--find-match'
                    : '';
                  const isAnchor = !!anchorCell && anchorCell.row === index && anchorCell.col === j;
                  return (
                    <td
                      key={j}
                      // Paired with the row's data-row-index so a cell can be
                      // addressed by (row, column) rather than by position.
                      // Hidden columns are skipped entirely, so counting `<td>`s
                      // would land on the wrong one.
                      data-col-index={j}
                      data-cell-key={findKey}
                      // Focusable only so a flip has somewhere to land. -1 keeps
                      // it out of the tab order: Tab is the view toggle here,
                      // and a grid whose every cell was a tab stop would take
                      // that away.
                      tabIndex={isAnchor ? -1 : undefined}
                      ref={isAnchor && !isEditing ? focusAnchorCell : undefined}
                      className={`${cellClass(isEdited, isEditing, selectedCells?.has(cellKey('r', index, j)))}${dtype ? ' dbm-td--date' : ''}${fkHere ? ' dbm-td--fk' : ''}${findClass}${typeColors.catClass(j, displayValue)}`}
                      onMouseDown={(e) => cellMouseDown(e, 'r', index, j)}
                      onDoubleClick={onCellOpen ? () => onCellOpen(index, j, cell) : undefined}
                      onContextMenu={onCellContextMenu ? (e) => onCellContextMenu(e, index, j) : undefined}
                    >
                      {isEditing && enumByColumn?.get(result.columns[j]) ? (
                        <DbEnumCellEditor
                          values={enumByColumn.get(result.columns[j]).values}
                          nullable={enumByColumn.get(result.columns[j]).nullable}
                          value={displayValue}
                          editingValue={editingValue}
                          onEditingValueChange={onEditingValueChange}
                          onCommitEdit={onCommitEdit}
                          onCancelEdit={onCancelEdit}
                          onEditNull={onEditNull}
                          onEditTab={onEditTab}
                        />
                      ) : isEditing ? (
                        // The hidden ghost keeps this cell's original text in
                        // layout so the auto-sized column can neither grow to
                        // the input's intrinsic width nor shrink when the text
                        // is swapped out — the grid stays pixel-identical
                        // while editing. The input overlays it, filling the
                        // cell's existing width.
                        <div className="dbm-edit-wrap">
                          <span className="dbm-edit-ghost" aria-hidden>
                            {displayValue === null ? 'NULL' : cellToText(displayValue)}
                          </span>
                          <input
                            type="text"
                            autoFocus
                            className="dbm-pending-input dbm-edit-input"
                            // Caret at the end: type-to-edit starts with the typed character, and
                            // F2 / double-click continue the value (Excel / DBeaver).
                            onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                            value={editingValue}
                            placeholder=""
                            onChange={(e) => onEditingValueChange(e.target.value)}
                            onBlur={onCommitEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                onCommitEdit();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                onCancelEdit();
                              } else if (e.key === 'Tab' && onEditTab) {
                                // Handled HERE because the tab's global Tab
                                // handler skips any input target — without
                                // this, Tab escaped to the browser's focus
                                // traversal and jumped past the whole table.
                                e.preventDefault();
                                onEditTab();
                              } else if (e.altKey && e.code === 'KeyN' && onEditNull) {
                                e.preventDefault();
                                onEditNull();
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <>
                          {renderCellValue(displayValue)}
                          {dtype && (
                            <DateEditButton
                              kind={dtype}
                              onOpen={(rect) => onDatePick('saved', index, j, rect)}
                            />
                          )}
                          {fkHere && displayValue !== null && (
                            <FkNavigateButton
                              title={`Go to ${fkHere.fk.refTable} (${fkHere.fk.name})`}
                              onNavigate={() => onFkNavigate(index, j)}
                            />
                          )}
                        </>
                      )}
                    </td>
                  );
                })}
              </tr>
              {/* A duplicate sits under the row it was copied from. */}
              {pendingUnderRow.get(index)?.map(renderPendingRow)}
              </React.Fragment>
            ))}
            {/* …and the rows below it. */}
            {padBottom > 0 && <tr aria-hidden style={{ height: padBottom }} />}
            </>
          )}
          {totalResultRows > MAX_RENDER_ROWS && (
            <tr>
              <td
                className="dbm-td"
                colSpan={colCount + 1}
                style={{ textAlign: 'center', fontStyle: 'italic', color: '#71717a' }}
              >
                Showing first {MAX_RENDER_ROWS.toLocaleString()} of {totalResultRows.toLocaleString()} rows
                — export for the full data
              </td>
            </tr>
          )}
          {trailingPending.map(renderPendingRow)}
          {loadingMore && (
            <tr>
              <td className="dbm-td dbm-loading-more" colSpan={colCount + 1}>
                <span className="dbm-spinner" aria-hidden /> Loading more rows…
              </td>
            </tr>
          )}
          {canAddRow && (
            <tr className="dbm-add-row-row">
              <td className="dbm-td" colSpan={colCount + 1}>
                <button type="button" className="dbm-add-row-btn" onClick={onAddRow}>
                  <Plus size={13} /> Add row
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </OsScroll>
    </>
  );
}
